import type { AppDefinition } from '@/types/app';
import type { AppPlan } from './plan/planTypes';
import { baseTheme } from '@/guidance/templates/theme';
import { applyVibeTheme } from '@/guidance/vibes';

/** True when this project is a published web app — opened in the browser rather than
 *  rendered by any in-app runtime. Absent `runtime` means 'engine' (every older record). */
export function isWebApp(def: Pick<AppDefinition, 'runtime'>): boolean {
  return def?.runtime === 'webapp';
}

/** A unique, readable id: the slug plus a random suffix so two apps with the same name
 *  never share a storage namespace on the shared publishing origin. */
export function newWebAppId(appName: string): string {
  const slug = appName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'web-app';
  return `${slug}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Wrap a generated web app in an AppDefinition-shaped record so it flows through the
 * existing storage, listing, chat and sync paths untouched — the same trick
 * `buildMiniAppRecord` uses. `screens` and `schemas` stay empty: a web app declares no
 * engine data model and renders itself.
 *
 * The theme is metadata only (a web app is styled by its own CSS), kept consistent so
 * anything reading a project's theme for a listing color chip treats this record like any
 * other.
 */
export function buildWebAppRecord(input: {
  /**
   * The app's stable id. It is ALSO the localStorage namespace baked into the published
   * document (`window.__SADDLE_APP__`), so it must be generated once and never derived
   * again: re-deriving it at publish time gave the build and the re-publish two different
   * namespaces, and every re-publish silently wiped the user's saved data.
   */
  id: string;
  appName: string;
  goal: string;
  /** The app body the model produced — markup + <style> + <script>. */
  source: string;
  /** Public URL, once published. */
  webUrl?: string;
  vibe?: string | null;
  /** The plan this was built from. Kept on the record because publishing needs its
   *  `rules`: a capacity chosen in the wizard is decorative until it reaches the schema
   *  the server enforces, and publish happens long after the plan screen is gone. */
  plan?: AppPlan | null;
  /**
   * The person's own app, or one they chose to share.
   *
   * Carried on the record because publishing happens from INSIDE the build, before
   * `finalizeBuild` stamps it — and `buildPublishInput` reads it to decide whether the app
   * appears in Discover.
   */
  kind?: 'personal' | 'sharable';
}): AppDefinition {
  const record: AppDefinition = {
    id: input.id,
    name: input.appName,
    description: input.goal,
    version: '1.0.0',
    theme: baseTheme,
    screens: [],
    schemas: [],
    navigation: { type: 'tabs', screens: [] },
    runtime: 'webapp',
    source: input.source,
    ...(input.kind ? { kind: input.kind } : {}),
    ...(input.webUrl ? { webUrl: input.webUrl } : {}),
    ...(input.plan ? { plan: input.plan } : {}),
  };
  return applyVibeTheme(record, input.vibe);
}


/**
 * An app whose build did not finish, and everything the person chose along the way.
 *
 * `finalizeBuild` creates the project and only runs on success, so a build that died at the
 * model call left nothing at all: the idea, the features they toggled, the layout, the look
 * and the sentence they typed under "Anything else" lived only in the wizard's own state,
 * and an error screen was the end of them.
 *
 * The rule this codebase already uses everywhere else applies here: write it down BEFORE
 * doing the thing that can fail. A project that exists and is marked costs one tap to
 * resume; a project that was never created costs the whole wizard again, from memory.
 */
export interface UnfinishedMark {
  /** When the attempt stopped. */
  at: string;
  /** What to tell them, in words they can act on. */
  reason: string;
}

/**
 * Whether this app still needs building.
 *
 * Reads the MARK, never the presence of source. A gate failure returns its last attempt, so
 * an app can hold plenty of text and still not be done — and judging by text would let a
 * rejected app look finished and be published.
 */
export function isUnfinished(def: { unfinished?: UnfinishedMark } | null | undefined): boolean {
  return !!def?.unfinished;
}

export function markUnfinished<T extends object>(def: T, reason: string): T & { unfinished: UnfinishedMark } {
  return { ...def, unfinished: { at: new Date().toISOString(), reason } };
}

/**
 * The build landed. The mark goes; the plan stays, because publishing still needs it.
 *
 * Returns `T`, not `Omit<T, 'unfinished'>`: the field is optional on the record, so its
 * absence is already expressible — and an Omit here would strip every other property off
 * the type for every caller, which is a lot of inconvenience to describe a value that is
 * simply not set.
 */
export function markFinished<T extends { unfinished?: UnfinishedMark }>(def: T): T {
  const { unfinished: _gone, ...rest } = def;
  return rest as T;
}
